import React, { useEffect, useState } from "react";
import {
  Drawer, Box, Typography, IconButton, Stack, Button, Skeleton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Select, FormControl, CircularProgress, Tooltip, Divider,
} from "@mui/material";
import {
  MdClose, MdEventAvailable, MdCancel, MdContentCopy, MdEdit,
  MdDownload, MdShare, MdDescription, MdCheckCircle, MdSchedule,
} from "react-icons/md";
import axios from "axios";
import { toast } from "react-toastify";
import { getProviderMeta } from "../../../utils/labProviders";
// Shared with the orders list, so a reschedule/cancel started from a row and
// one started from this drawer are literally the same flow.
import { RescheduleDialog, CancelDialog, canModifyOrder } from "./LabOrderActionDialogs";

const BASE = "https://api.medstown.com";

// Backend sends a `reports` array; each item carries a signed
// `tokenize_public_file_url`. We only surface that field, and de-dupe so the
// same report never shows twice.
function normalizeReports(order) {
  const items = Array.isArray(order?.reports) ? order.reports : [];
  const out = [];
  const seen = new Set();
  items.forEach((item, idx) => {
    if (!item || typeof item !== "object") return;
    const url = item.tokenize_public_file_url;
    if (!url || typeof url !== "string" || seen.has(url)) return;
    seen.add(url);
    const name =
      item.name || item.patientName || item.patient_name ||
      item.testName || item.test_name || item.title || `Report ${idx + 1}`;
    out.push({ name: String(name), url });
  });
  return out;
}

async function shareReport({ name, url }, bookingId) {
  const title = `Lab report${bookingId ? ` · ${bookingId}` : ""}`;
  const text = `${name}: ${url}`;
  if (navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return;
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Report link copied");
  } catch {
    toast.error("Couldn't share the report link");
  }
}

const T = {
  surface:    "#ffffff",
  bg:         "#f8fafc",
  border:     "#e5e7eb",
  borderSoft: "#f1f5f9",
  ink:        "#0f172a",
  muted:      "#64748b",
  subtle:     "#94a3b8",
  accent:     "#4f46e5",
  green:      "#059669",
  amber:      "#d97706",
  red:        "#dc2626",
  violet:     "#7c3aed",
};

const STATUS_TONE = {
  Scheduled:             T.accent,
  Confirmed:             "#0284c7",
  Assigned:              "#0891b2",
  SampleCollected:       T.violet,
  ReportGenerated:       T.green,
  Completed:             T.green,
  Rescheduled:           T.amber,
  CancellationRequested: T.amber,
  Cancelled:             T.red,
};

const STATUS_OPTIONS = [
  "Scheduled", "Confirmed", "Assigned", "SampleCollected",
  "ReportGenerated", "Completed", "Rescheduled",
  "CancellationRequested", "Cancelled",
];

const inr = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

// ── Primitives ──────────────────────────────────────────────────────────────
function Section({ label, children, action }) {
  return (
    <Box sx={{ mb: 2.2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography sx={{
          fontSize: "0.62rem", fontWeight: 700, color: T.muted,
          textTransform: "uppercase", letterSpacing: "0.08em",
        }}>
          {label}
        </Typography>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

function Field({ label, value, mono, copyable, onCopy }) {
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.2} sx={{
      py: 1, borderBottom: `1px solid ${T.borderSoft}`,
      "&:last-child": { borderBottom: 0 },
    }}>
      <Typography sx={{
        fontSize: "0.7rem", fontWeight: 500, color: T.muted,
        width: 120, flexShrink: 0, pt: 0.15,
      }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: "0.78rem", fontWeight: 500, color: T.ink,
          wordBreak: "break-word",
          fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined,
          fontVariantNumeric: "tabular-nums",
        }}>
          {value == null || value === "" ? <Box component="span" sx={{ color: T.subtle }}>—</Box> : value}
        </Typography>
      </Box>
      {copyable && value && (
        <IconButton size="small" onClick={() => onCopy(value, label)}
          sx={{ p: 0.3, color: T.subtle, "&:hover": { color: T.accent } }}>
          <MdContentCopy size={12} />
        </IconButton>
      )}
    </Stack>
  );
}

function StatusBadge({ status }) {
  const tone = STATUS_TONE[status] || T.muted;
  return (
    <Stack direction="row" alignItems="center" spacing={0.7} sx={{
      display: "inline-flex",
      px: 1, py: 0.35, borderRadius: 1.2,
      bgcolor: `${tone}0d`,
      border: `1px solid ${tone}33`,
    }}>
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: tone }} />
      <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: tone }}>
        {status || "—"}
      </Typography>
    </Stack>
  );
}

// ── Status override Dialog ──────────────────────────────────────────────────
function StatusDialog({ open, onClose, order, onDone }) {
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote]           = useState("");
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    if (open && order) { setNewStatus(order.status || ""); setNote(""); }
  }, [open, order]);

  async function submit() {
    if (!newStatus) { toast.error("Pick a status"); return; }
    setSaving(true);
    try {
      await axios.patch(`${BASE}/admin/lab/orders/${order._id}/status`, {
        status: newStatus, note,
      });
      toast.success("Status updated");
      onDone(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || "Update failed");
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, border: `1px solid ${T.border}` } }}>
      <DialogTitle sx={{ fontSize: "0.95rem", fontWeight: 700, color: T.ink, pt: 2.2, pb: 0.5 }}>
        Override status
      </DialogTitle>
      <DialogContent sx={{ pt: 1.2, pb: 1 }}>
        <Typography sx={{ fontSize: "0.74rem", color: T.muted, mb: 1.5 }}>
          Manual override — does not call any partner API. Logged in order history.
        </Typography>
        <Stack spacing={1.2}>
          <FormControl size="small" fullWidth>
            <Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
              {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label="Admin note" value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline rows={2} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={onClose} sx={{ color: T.muted, textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained" onClick={submit} disabled={saving}
          sx={{
            textTransform: "none", fontWeight: 600, borderRadius: 1.5,
            bgcolor: T.ink, "&:hover": { bgcolor: "#1e293b" },
            boxShadow: "none",
          }}>
          {saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main Drawer ─────────────────────────────────────────────────────────────
export default function LabOrderDetailDrawer({ orderId, onClose, onMutated }) {
  const [order, setOrder]       = useState(null);
  const [loading, setLoading]   = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);

  const open = !!orderId;

  useEffect(() => {
    if (!orderId) { setOrder(null); return; }
    let cancelled = false;
    setLoading(true);
    axios.get(`${BASE}/admin/lab/orders/${orderId}`)
      .then(({ data }) => { if (!cancelled) setOrder(data.order || null); })
      .catch(() => { if (!cancelled) toast.error("Failed to load order"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orderId]);

  async function refresh() {
    if (!orderId) return;
    try {
      const { data } = await axios.get(`${BASE}/admin/lab/orders/${orderId}`);
      setOrder(data.order || null);
      onMutated?.();
    } catch { /* ignore */ }
  }

  function copy(val, label) {
    if (!val) return;
    navigator.clipboard.writeText(String(val));
    toast.success(`${label} copied`);
  }

  const canModify = canModifyOrder(order);
  const addr = order?.address;

  return (
    <>
      <Drawer
        anchor="right" open={open} onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 500, md: 560 },
            bgcolor: T.bg,
            borderLeft: `1px solid ${T.border}`,
          },
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 2.5, py: 2,
          bgcolor: T.surface,
          borderBottom: `1px solid ${T.border}`,
        }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{
                fontSize: "0.62rem", fontWeight: 700, color: T.muted,
                textTransform: "uppercase", letterSpacing: "0.08em",
              }}>
                Lab order
              </Typography>
              {order && (
                <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mt: 0.3 }}>
                  <Typography sx={{
                    fontSize: "1.1rem", fontWeight: 700, color: T.ink,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    letterSpacing: "-0.01em",
                  }}>
                    {order.medstownBookingId}
                  </Typography>
                  <IconButton size="small" onClick={() => copy(order.medstownBookingId, "Booking ID")}
                    sx={{ p: 0.3, color: T.subtle, "&:hover": { color: T.accent } }}>
                    <MdContentCopy size={12} />
                  </IconButton>
                </Stack>
              )}
              {order && (
                <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mt: 0.8 }}>
                  <StatusBadge status={order.status} />
                  <Typography sx={{ fontSize: "0.72rem", color: T.muted }}>
                    {fmtDate(order.createdAt)}
                  </Typography>
                </Stack>
              )}
            </Box>
            <IconButton onClick={onClose}
              sx={{
                color: T.muted, ml: 1,
                "&:hover": { color: T.ink, bgcolor: T.borderSoft },
              }}>
              <MdClose />
            </IconButton>
          </Stack>

          {order && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button
                size="small" startIcon={<MdEventAvailable size={15} />}
                disabled={!canModify}
                onClick={() => setRescheduleOpen(true)}
                sx={{
                  flex: 1, textTransform: "none", fontSize: "0.78rem",
                  fontWeight: 600, borderRadius: 1.5, py: 0.85,
                  color: T.ink, bgcolor: T.surface,
                  border: `1px solid ${T.border}`,
                  "&:hover": { bgcolor: T.borderSoft, borderColor: T.subtle },
                  "&.Mui-disabled": { color: T.subtle, borderColor: T.borderSoft },
                }}
              >
                Reschedule
              </Button>
              <Button
                size="small" startIcon={<MdCancel size={15} />}
                disabled={!canModify}
                onClick={() => setCancelOpen(true)}
                sx={{
                  flex: 1, textTransform: "none", fontSize: "0.78rem",
                  fontWeight: 600, borderRadius: 1.5, py: 0.85,
                  color: T.red, bgcolor: T.surface,
                  border: `1px solid ${T.red}33`,
                  "&:hover": { bgcolor: `${T.red}0d`, borderColor: `${T.red}55` },
                  "&.Mui-disabled": { color: T.subtle, borderColor: T.borderSoft },
                }}
              >
                Cancel
              </Button>
              <Tooltip title="Override status">
                <IconButton
                  onClick={() => setStatusOpen(true)}
                  sx={{
                    color: T.muted, bgcolor: T.surface,
                    border: `1px solid ${T.border}`, borderRadius: 1.5,
                    width: 36, height: 36,
                    "&:hover": { color: T.accent, borderColor: `${T.accent}55`, bgcolor: `${T.accent}0d` },
                  }}
                >
                  <MdEdit size={15} />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Box>

        {/* Body */}
        <Box sx={{ p: 2.5, overflowY: "auto", flex: 1 }}>
          {loading ? (
            <Stack spacing={1.5}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={90} sx={{ borderRadius: 2 }} />
              ))}
            </Stack>
          ) : !order ? (
            <Typography sx={{ textAlign: "center", color: T.muted, py: 4 }}>Order not found</Typography>
          ) : (
            <>
              {/* Summary card */}
              <Box sx={{
                p: 2, borderRadius: 2.5, mb: 2,
                bgcolor: T.surface,
                border: `1px solid ${T.border}`,
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: "0.62rem", color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      Total
                    </Typography>
                    <Typography sx={{
                      fontSize: "1.5rem", fontWeight: 700, color: T.ink,
                      fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em",
                      lineHeight: 1.1, mt: 0.2,
                    }}>
                      {inr(order.total)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ fontSize: "0.62rem", color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      Partner
                    </Typography>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: T.ink, mt: 0.2 }}>
                      {getProviderMeta(order.provider).label}
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ borderColor: T.borderSoft, mb: 1.2 }} />

                <Stack direction="row" spacing={3}>
                  <Box>
                    <Typography sx={{ fontSize: "0.62rem", color: T.muted, fontWeight: 600, textTransform: "uppercase" }}>
                      Scheduled
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: T.ink, mt: 0.2 }}>
                      {order.scheduledDate || "—"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.68rem", color: T.muted }}>
                      {order.scheduledSlot || "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "0.62rem", color: T.muted, fontWeight: 600, textTransform: "uppercase" }}>
                      Collection
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: T.ink, mt: 0.2 }}>
                      {order.collectionType === "lab" ? "Lab visit" : "Home"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "0.62rem", color: T.muted, fontWeight: 600, textTransform: "uppercase" }}>
                      Payment
                    </Typography>
                    <Typography sx={{
                      fontSize: "0.78rem", fontWeight: 600, mt: 0.2,
                      color: order.paymentStatus === "paid" ? T.green : T.amber,
                      textTransform: "capitalize",
                    }}>
                      {order.paymentStatus || "—"}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Report */}
              {(() => {
                const reports = normalizeReports(order);
                const generated = reports.length > 0;
                return (
                  <Box sx={{
                    p: 2, borderRadius: 2.5, mb: 2,
                    bgcolor: T.surface,
                    border: `1px solid ${generated ? `${T.green}55` : T.border}`,
                  }}>
                    <Section
                      label="Report"
                      action={
                        <Stack direction="row" alignItems="center" spacing={0.6} sx={{
                          px: 1, py: 0.3, borderRadius: 1.2,
                          bgcolor: generated ? `${T.green}0d` : `${T.amber}0d`,
                          border: `1px solid ${generated ? `${T.green}33` : `${T.amber}33`}`,
                        }}>
                          {generated
                            ? <MdCheckCircle size={13} color={T.green} />
                            : <MdSchedule size={13} color={T.amber} />}
                          <Typography sx={{
                            fontSize: "0.68rem", fontWeight: 700,
                            color: generated ? T.green : T.amber,
                          }}>
                            {generated ? `Generated · ${reports.length}` : "Not generated yet"}
                          </Typography>
                        </Stack>
                      }
                    >
                      {!generated ? (
                        <Typography sx={{ fontSize: "0.76rem", color: T.muted }}>
                          The lab report isn't available yet. It appears here automatically once the partner uploads it.
                        </Typography>
                      ) : (
                        <Stack spacing={0.8}>
                          {reports.map((r, i) => (
                            <Stack key={i} direction="row" alignItems="center" spacing={1}
                              sx={{
                                py: 0.9, px: 1.2, borderRadius: 1.5,
                                bgcolor: T.bg,
                                border: `1px solid ${T.borderSoft}`,
                              }}>
                              <MdDescription size={16} color={T.green} />
                              <Typography sx={{
                                flex: 1, minWidth: 0, fontSize: "0.78rem",
                                fontWeight: 600, color: T.ink,
                              }} noWrap title={r.name}>
                                {r.name}
                              </Typography>
                              <Tooltip title="Download / open">
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={r.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    p: 0.5, color: T.green,
                                    "&:hover": { bgcolor: `${T.green}14` },
                                  }}
                                >
                                  <MdDownload size={16} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Share">
                                <IconButton
                                  size="small"
                                  onClick={() => shareReport(r, order.medstownBookingId)}
                                  sx={{
                                    p: 0.5, color: T.accent,
                                    "&:hover": { bgcolor: `${T.accent}14` },
                                  }}
                                >
                                  <MdShare size={15} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          ))}
                        </Stack>
                      )}
                    </Section>
                  </Box>
                );
              })()}

              {/* Patients */}
              <Box sx={{
                p: 2, borderRadius: 2.5, mb: 2,
                bgcolor: T.surface,
                border: `1px solid ${T.border}`,
              }}>
                <Section label={`Patients · ${order.patients?.length || 0}`}>
                  <Stack spacing={1.2}>
                    {order.patients?.map((p, i) => (
                      <Stack key={i} direction="row" alignItems="center" spacing={1.2}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: T.ink }} noWrap>
                            {p.name}
                          </Typography>
                          <Typography sx={{ fontSize: "0.7rem", color: T.muted }} noWrap>
                            {p.gender} · {p.age} yrs · {p.relation}
                          </Typography>
                        </Box>
                        {p.phone && (
                          <Typography sx={{
                            fontSize: "0.74rem", color: T.ink, fontWeight: 500,
                            fontVariantNumeric: "tabular-nums",
                          }}>
                            {p.phone}
                          </Typography>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </Section>
              </Box>

              {/* Cart items */}
              <Box sx={{
                p: 2, borderRadius: 2.5, mb: 2,
                bgcolor: T.surface,
                border: `1px solid ${T.border}`,
              }}>
                <Section label={`Tests & packages · ${order.cartItems?.length || 0}`}>
                  <Stack spacing={0.8}>
                    {order.cartItems?.map((it, i) => (
                      <Stack key={i} direction="row" justifyContent="space-between" alignItems="center"
                        sx={{
                          py: 1, px: 1.2, borderRadius: 1.5,
                          bgcolor: T.bg,
                          border: `1px solid ${T.borderSoft}`,
                        }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: T.ink }} noWrap>
                            {it.name}
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 0.2 }}>
                            <Box component="span" sx={{
                              fontSize: "0.6rem", fontWeight: 700,
                              px: 0.6, py: 0.1, borderRadius: 0.6,
                              bgcolor: it.isPackage ? `${T.violet}14` : `${T.accent}14`,
                              color: it.isPackage ? T.violet : T.accent,
                              textTransform: "uppercase",
                            }}>
                              {it.isPackage ? "Package" : "Test"}
                            </Box>
                            <Typography sx={{ fontSize: "0.64rem", color: T.muted, fontFamily: "ui-monospace, Menlo, monospace" }}>
                              {it.externalId}
                            </Typography>
                          </Stack>
                        </Box>
                        <Box sx={{ textAlign: "right", ml: 1 }}>
                          <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: T.ink, fontVariantNumeric: "tabular-nums" }}>
                            {inr(it.discountedPrice ?? it.price)}
                          </Typography>
                          {it.discountedPrice != null && it.discountedPrice < it.price && (
                            <Typography sx={{
                              fontSize: "0.62rem", color: T.subtle,
                              textDecoration: "line-through", fontVariantNumeric: "tabular-nums",
                            }}>
                              {inr(it.price)}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    ))}
                  </Stack>

                  <Stack spacing={0.4} sx={{
                    mt: 1.5, pt: 1.2, borderTop: `1px dashed ${T.border}`,
                  }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontSize: "0.74rem", color: T.muted }}>Subtotal</Typography>
                      <Typography sx={{ fontSize: "0.74rem", color: T.ink, fontVariantNumeric: "tabular-nums" }}>
                        {inr(order.subtotal)}
                      </Typography>
                    </Stack>
                    {order.discountAmount > 0 && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ fontSize: "0.74rem", color: T.muted }}>Discount</Typography>
                        <Typography sx={{ fontSize: "0.74rem", color: T.green, fontVariantNumeric: "tabular-nums" }}>
                          −{inr(order.discountAmount)}
                        </Typography>
                      </Stack>
                    )}
                    <Stack direction="row" justifyContent="space-between" sx={{ pt: 0.4 }}>
                      <Typography sx={{ fontSize: "0.78rem", color: T.ink, fontWeight: 700 }}>Total</Typography>
                      <Typography sx={{ fontSize: "0.82rem", color: T.ink, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                        {inr(order.total)}
                      </Typography>
                    </Stack>
                  </Stack>
                </Section>
              </Box>

              {/* Address */}
              {addr && (
                <Box sx={{
                  p: 2, borderRadius: 2.5, mb: 2,
                  bgcolor: T.surface, border: `1px solid ${T.border}`,
                }}>
                  <Section label="Address">
                    <Typography sx={{ fontSize: "0.78rem", color: T.ink, lineHeight: 1.5 }}>
                      {typeof addr === "string" ? addr : JSON.stringify(addr)}
                    </Typography>
                    {order.pincode && (
                      <Typography sx={{ fontSize: "0.72rem", color: T.muted, mt: 0.6 }}>
                        Pincode · <Box component="span" sx={{ fontFamily: "ui-monospace, Menlo, monospace", color: T.ink }}>{order.pincode}</Box>
                      </Typography>
                    )}
                    {order.location?.latitude && (
                      <Typography sx={{
                        fontSize: "0.7rem", color: T.muted, mt: 0.3,
                        fontFamily: "ui-monospace, Menlo, monospace",
                      }}>
                        {order.location.latitude}, {order.location.longitude}
                      </Typography>
                    )}
                  </Section>
                </Box>
              )}

              {/* Provider details */}
              <Box sx={{
                p: 2, borderRadius: 2.5, mb: 2,
                bgcolor: T.surface, border: `1px solid ${T.border}`,
              }}>
                <Section label="Partner details">
                  {/* Only the fields this partner actually populates — Orange and
                      Redcliffe track bookings quite differently. An unknown
                      provider contributes no fields rather than borrowing
                      another partner's. */}
                  {getProviderMeta(order.provider).detailFields.map((f) => {
                    const raw = f.value ? f.value(order) : order[f.key];
                    let value = raw;
                    if (raw != null && raw !== "") {
                      if (f.type === "date") value = fmtDate(raw);
                      else if (f.type === "link") {
                        value = (
                          <Box
                            component="a"
                            href={raw}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: T.accent, fontWeight: 600,
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            Track phlebotomist
                          </Box>
                        );
                      }
                    }
                    return (
                      <Field
                        key={f.key}
                        label={f.label}
                        value={value}
                        mono={f.mono}
                        copyable={f.copyable}
                        onCopy={f.copyable ? () => copy(raw, f.label) : undefined}
                      />
                    );
                  })}
                  <Field label="Razorpay payment" value={order.razorpayPaymentId} mono copyable onCopy={copy} />
                  <Field label="Razorpay order" value={order.razorpayOrderId} mono copyable onCopy={copy} />
                </Section>
              </Box>

              {/* Timeline */}
              {order.webhookHistory?.length > 0 && (
                <Box sx={{
                  p: 2, borderRadius: 2.5, mb: 2,
                  bgcolor: T.surface, border: `1px solid ${T.border}`,
                }}>
                  <Section label={`Activity · ${order.webhookHistory.length}`}>
                    <Box sx={{ position: "relative", p:2, maxHeight: 280, overflowY: "auto" }}>
                      <Box sx={{
                        position: "absolute", left: 6, top: 6, bottom: 6,
                        width: 1,
                      }} />
                      {[...order.webhookHistory].reverse().map((w, i) => {
                        const tone = STATUS_TONE[w.status] || T.muted;
                        return (
                          <Box key={i} sx={{ position: "relative", pb: 1.2, "&:last-child": { pb: 0 } }}>
                            <Box sx={{
                              position: "absolute", left: -14, top: 6,
                              width: 10, height: 10, borderRadius: "50%",
                              bgcolor: T.surface,
                              border: `2px solid ${tone}`,
                            }} />
                            <Stack direction="row" justifyContent="space-between">
                              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: T.ink }}>
                                {w.event}
                              </Typography>
                              <Typography sx={{ fontSize: "0.66rem", color: T.subtle, fontVariantNumeric: "tabular-nums" }}>
                                {fmtDate(w.receivedAt)}
                              </Typography>
                            </Stack>
                            {w.status && (
                              <Typography sx={{ fontSize: "0.68rem", color: tone, fontWeight: 500, mt: 0.1 }}>
                                → {w.status}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Section>
                </Box>
              )}
            </>
          )}
        </Box>
      </Drawer>

      <RescheduleDialog open={rescheduleOpen} onClose={() => setRescheduleOpen(false)} order={order} onDone={refresh} />
      <CancelDialog open={cancelOpen} onClose={() => setCancelOpen(false)} order={order} onDone={refresh} />
      <StatusDialog open={statusOpen} onClose={() => setStatusOpen(false)} order={order} onDone={refresh} />
    </>
  );
}
